import { AfterViewInit, Component, ElementRef, ViewChild } from "@angular/core";
import { ComponentInteractionService } from "src/app/services/component-interaction/component-interaction.service";
import { CompassConfig, CompassLabels } from "~/src/app/shared/models/compass.model";
import { ColorCodes } from "@utils/color-constants";
import * as THREE from "three";
import { ColorRepresentation } from "three";
import { InteractionService } from "../../shared/helpers/interaction.service";
import { Face } from "~/src/utils/count-calculator/count-calculator.model";

@Component({
    selector: "compass-cube",
    templateUrl: "./compass-cube.component.html",
    styleUrls: ["./compass-cube.component.scss"],
    standalone: false,
})
export class CompassCubeComponent implements AfterViewInit {
    @ViewChild("compassCanvas", { static: true }) compassCanvas!: ElementRef<HTMLCanvasElement>;

    private renderer!: THREE.WebGLRenderer;
    private scene: THREE.Scene;
    private camera: THREE.PerspectiveCamera;
    private compassNavigationCube!: THREE.Mesh;

    constructor(
        private componentInteractionService: ComponentInteractionService,
        private interactionService: InteractionService,
    ) {
        this.scene = new THREE.Scene();
        this.camera = new THREE.PerspectiveCamera(
            CompassConfig.cameraConfig.fov,
            CompassConfig.RenderConfig.width / CompassConfig.RenderConfig.height, // width by height gives proper angle for camera
        );
    }

    ngAfterViewInit(): void {
        this.canvasConfiguration();
        this.componentInteractionService.getCameraPosition().subscribe((position) => {
            const orbitControls = this.interactionService.getOrbitControls();
            if (!orbitControls) return;
            this.updateCompassCubeRotation(position, orbitControls.target);
            this.render();
        });
    }

    private canvasConfiguration(): void {
        this.renderer = new THREE.WebGLRenderer({
            canvas: this.compassCanvas.nativeElement,
            alpha: true,
            preserveDrawingBuffer: true,
            antialias: true,
        });
        this.renderer.setSize(CompassConfig.RenderConfig.width, CompassConfig.RenderConfig.height);
        this.renderer.setPixelRatio(window.devicePixelRatio);
        this.camera.position.z = CompassConfig.RenderConfig.cameraPositionZ;
        const ambientLight = new THREE.AmbientLight(ColorCodes.white, 1); // 1 is the intensity of the light.
        this.scene.add(ambientLight);
        this.scene.add(this.camera);
        this.createCompassNavigationCube();
    }

    private render(): void {
        this.renderer.render(this.scene, this.camera);
    }

    // Creates a cube for navigation.
    private createCompassNavigationCube(): void {
        const compassGeometry = new THREE.BoxGeometry(
            CompassConfig.cubeSize,
            CompassConfig.cubeSize,
            CompassConfig.cubeSize,
        );
        const compassMesh = new THREE.Mesh(compassGeometry, this.createCompassNavigationMaterials());
        const outlineContainer = new THREE.BoxHelper(compassMesh);
        compassMesh.add(outlineContainer);
        this.scene.add(compassMesh);
        this.compassNavigationCube = compassMesh;
    }

    // This will append the text on the cube representing the faces.
    private createCompassNavigationMaterials(): THREE.MeshBasicMaterial[] {
        const materials: THREE.MeshBasicMaterial[] = [];

        const labels = CompassLabels;

        labels.forEach((label) => {
            const canvas = document.createElement("canvas");
            const context = canvas.getContext("2d");
            if (context) {
                canvas.width = CompassConfig.canvasWidth;
                canvas.height = CompassConfig.canvasHeight;
                context.fillStyle = CompassConfig.cubeColor;
                context.fillRect(0, 0, canvas.width, canvas.height);

                context.font = CompassConfig.cubeFont;
                context.fillStyle = CompassConfig.textColor;

                const textMetrics = context.measureText(label);
                const textWidth = textMetrics.width;
                const x = (canvas.width - textWidth) / 2; // Calculate the centered position
                const y = canvas.height / 2; // Vertically center

                context.fillText(label, x, y);

                const texture = new THREE.CanvasTexture(canvas);
                const material = new THREE.MeshBasicMaterial({
                    map: texture,
                    color: CompassConfig.textColor as ColorRepresentation,
                    transparent: true,
                    opacity: 0.4,
                });
                materials.push(material);
            }
        });

        return materials;
    }

    private updateCompassCubeRotation(cameraPosition: THREE.Vector3, controlsTarget: THREE.Vector3): void {
        // Create a vector from the camera to the target
        const offset = new THREE.Vector3().copy(cameraPosition).sub(controlsTarget);
        // Convert to spherical coordinates
        const spherical = new THREE.Spherical().setFromVector3(offset);

        this.compassNavigationCube.rotation.set(0, 0, 0);
        this.compassNavigationCube.rotation.x = THREE.MathUtils.degToRad(90) - spherical.phi;
        this.compassNavigationCube.rotation.y = -spherical.theta + THREE.MathUtils.degToRad(90);

        const currentFaceDirection = this.getCurrentFaceDirection(spherical);
        this.componentInteractionService.setCurrentFacingDirection(currentFaceDirection);
    }

    // Function to determine which direction the camera is facing using spherical coordinates
    private getCurrentFaceDirection(spherical: THREE.Spherical): Face {
        const thetaDeg = THREE.MathUtils.radToDeg(spherical.theta); // Azimuthal angle (horizontal)
        const phiDeg = THREE.MathUtils.radToDeg(spherical.phi); // Polar angle (vertical)

        // First check if we're looking primarily up or down
        if (phiDeg < 45) {
            return Face.Top; // Looking mostly upward
        } else if (phiDeg > 135) {
            return Face.Bottom; // Looking mostly downward
        }

        // Normalize thetaDeg to 0–360
        const thetaNormalized = (thetaDeg + 360) % 360;

        if (thetaNormalized >= 315 || thetaNormalized < 45) {
            return Face.Left;
        } else if (thetaNormalized >= 45 && thetaNormalized < 135) {
            return Face.Front;
        } else if (thetaNormalized >= 135 && thetaNormalized < 225) {
            return Face.Right;
        } else {
            return Face.Back;
        }
    }
}
